import { useState, useMemo, useCallback, useEffect } from 'react';
import { myTeamApi, DEPARTMENT_OPTIONS } from '../api/myTeamApi';
import { appToast } from '../../../../components/AppToast';

export function useMyTeamList() {
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchField, setSearchField] = useState('Name');
  const [searchText, setSearchText] = useState('');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const statusOptions = [
    { value: 'All', label: 'All Members' },
    { value: 'Active', label: 'Active' },
    { value: 'On Leave', label: 'On Leave' },
  ];

  const searchFieldOptions = [
    { value: 'Name', label: 'Name' },
    { value: 'Email', label: 'Email ID' },
    { value: 'Mobile', label: 'Mobile No' },
    { value: 'Role', label: 'Role / Designation' },
  ];

  const tools = [
    { key: 'pdf', label: 'Export Team Directory to PDF', icon: 'picture_as_pdf' },
    { key: 'excel', label: 'Export Team Directory to Excel', icon: 'table_view' },
    { key: 'print', label: 'Print Team Directory', icon: 'print' },
  ];

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await myTeamApi.getList();
      setRecords(res.data || []);
    } catch {
      // Fallback
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      // Department filter
      if (departmentFilter !== 'All' && item.department !== departmentFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'All' && item.status !== statusFilter) {
        return false;
      }

      // Text search
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        if (searchField === 'Name' && !item.name?.toLowerCase().includes(query)) return false;
        if (searchField === 'Email' && !item.email?.toLowerCase().includes(query)) return false;
        if (searchField === 'Mobile' && !item.mobile?.includes(query)) return false;
        if (searchField === 'Role' && !item.role?.toLowerCase().includes(query)) return false;
      }

      return true;
    });
  }, [records, departmentFilter, statusFilter, searchText, searchField]);

  // Summary statistics
  const summary = useMemo(() => {
    const totalMembers = filteredRecords.length;
    const activeCount = filteredRecords.filter((r) => r.status === 'Active').length;
    const departments = new Set(filteredRecords.map((r) => r.department).filter(Boolean));
    const locations = new Set(filteredRecords.map((r) => r.location).filter(Boolean));

    return {
      totalMembers,
      activeCount,
      departmentCount: departments.size,
      locationCount: locations.size,
    };
  }, [filteredRecords]);

  // Pagination
  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Actions
  const handleSearch = useCallback(() => {
    setPage(1);
    appToast.success('Team records filtered');
  }, []);

  const resetFilters = useCallback(() => {
    setDepartmentFilter('All');
    setStatusFilter('All');
    setSearchText('');
    setSearchField('Name');
    setPage(1);
  }, []);

  const handleToolSelect = useCallback((toolKey) => {
    const key = String(toolKey || '').toLowerCase();
    if (key.includes('print')) {
      window.print();
    } else if (key.includes('pdf')) {
      appToast.success('Exporting Team Directory to PDF...');
    } else if (key.includes('excel')) {
      appToast.success('Exporting Team Directory to Excel...');
    }
  }, []);

  return {
    state: {
      records: paginatedRecords,
      allFilteredRecords: filteredRecords,
      totalCount,
      page,
      pageSize,
      totalPages,
      departmentFilter,
      statusFilter,
      searchField,
      searchText,
      isLoading,
      departmentOptions: DEPARTMENT_OPTIONS,
      statusOptions,
      searchFieldOptions,
      tools,
      summary,
    },
    actions: {
      setDepartmentFilter,
      setStatusFilter,
      setSearchField,
      setSearchText,
      setPage,
      setPageSize,
      handleSearch,
      resetFilters,
      handleToolSelect,
    },
  };
}
